import {useCallback, useEffect} from 'react';
import {useVisitsStore} from '../store/visitsStore';
import {useVisitsUIStore} from '../store/visitsUIStore';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {Visit, VisitFilters} from '../models/visit.model';

/**
 * Unified hook for visits management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 * Note: Uses visitsStore directly (not useVisitStatistics) to avoid duplicate fetchVisits on mount
 */
export const useVisitsManagement = () => {
  // Data stores
  const visitsStore = useVisitsStore();
  const uiStore = useVisitsUIStore();

  // Destructure from visitsStore (includes statistics - no need for useVisitStatistics)
  const {
    visits, visitDetails, pagination, statistics: statsData,
    loading, loadingDetails, loadingStatistics: statsLoading,
    error, detailsError, statisticsError: statsError,
    fetchVisits, fetchVisitDetails, fetchStatistics: fetchStats, fetchTrends,
    approveVisit, rejectVisit,
    refreshData, clearErrors, clearVisitDetails
  } = visitsStore;

  // Destructure from uiStore
  const {
    activeTab, setActiveTab,
    isDetailsModalOpen, selectedVisit, openDetailsModal, closeDetailsModal,
    selectedPeriod, selectedStore, dateFrom, dateTo,
    setSelectedPeriod, setSelectedStore, setDateFrom, setDateTo, clearAnalyticsFilters,
    lastVisit, visitHistory, storeVisitsLoading, storeVisitsError,
    setLastVisit, setVisitHistory, setStoreVisitsLoading, setStoreVisitsError, clearStoreVisits,
    actionError, actionErrorDetails, setActionError, clearActionError,
    actionLoading, setActionLoading,
    reset: resetUI
  } = uiStore;

  // Unified Actions
  const handleTabChange = useCallback((tab: 'list' | 'analytics') => {
    setActiveTab(tab);
    clearErrors();
    if (tab === 'list') {
      fetchVisits(visitsStore.filters, 1, pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
    }
  }, [setActiveTab, clearErrors, fetchVisits, visitsStore.filters, pagination?.limit]);

  // Initial fetch — قائمة الزيارات تبويب افتراضي
  useEffect(() => {
    fetchVisits(visitsStore.filters, 1, PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  const handleVisitClick = useCallback((visit: Visit) => {
    openDetailsModal(visit);
  }, [openDetailsModal]);

  const handleCloseDetailsModal = useCallback(() => {
    closeDetailsModal();
  }, [closeDetailsModal]);

  const handleRefresh = useCallback(() => {
    if (activeTab === 'analytics') {
      fetchTrends(); // VisitsAnalytics displays trends
    } else {
      refreshData();
    }
  }, [activeTab, fetchTrends, refreshData]);

  const handlePageChange = useCallback((newPage: number) => {
    const currentLimit = pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
    fetchVisits(visitsStore.filters, newPage, currentLimit);
  }, [fetchVisits, visitsStore.filters, pagination?.limit]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    fetchVisits(visitsStore.filters, 1, newPageSize);
  }, [fetchVisits, visitsStore.filters]);

  const handleStatusTabChange = useCallback((status: 'all' | 'pending' | 'approved' | 'rejected') => {
    const statusValue = status === 'all' ? undefined : status;
    const newFilters = {...visitsStore.filters, status: statusValue};
    visitsStore.setFilters(newFilters);
    fetchVisits(newFilters, 1, pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  }, [fetchVisits, visitsStore, pagination?.limit]);

  // Toolbar filters (brand / developer / store / date period). Merge into the
  // existing filters so the active status tab is preserved, then reset to page 1.
  // A field passed as `undefined` clears it (the data source omits undefined params).
  const applyFilters = useCallback((values: Partial<VisitFilters>) => {
    const newFilters = {...visitsStore.filters, ...values};
    visitsStore.setFilters(newFilters);
    fetchVisits(newFilters, 1, pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  }, [fetchVisits, visitsStore, pagination?.limit]);

  // Clear all toolbar filters but keep the active status tab.
  const clearFilters = useCallback(() => {
    const {status} = visitsStore.filters;
    const newFilters: VisitFilters = status ? {status} : {};
    visitsStore.setFilters(newFilters);
    fetchVisits(newFilters, 1, pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  }, [fetchVisits, visitsStore, pagination?.limit]);

  // Approve/Reject handlers
  const handleApproveVisit = useCallback(async (visit: Visit) => {
    if (!visit || !visit.id) {
      console.error('Invalid visit object for approval');
      return;
    }

    clearActionError();
    setActionLoading({ approve: true });

    try {
      await approveVisit(visit.id);
      // Close modal after successful approval
      closeDetailsModal();
      // Refresh data to show updated status
      await refreshData();
    } catch (error) {
      const errorHandler = StandardizedErrorHandler.getInstance();
      const appError = errorHandler.toAppError(error);
      let errorDetails: { product_id?: number; current_quantity?: number; reported_quantity?: number } | null = null;
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.error) {
          errorDetails = axiosError.response.data.error;
        }
      }
      setActionError(appError.message, errorDetails);
      errorHandler.handleError(error);
    } finally {
      setActionLoading({ approve: false });
    }
  }, [approveVisit, closeDetailsModal, refreshData, clearActionError, setActionError, setActionLoading]);

  const handleRejectVisit = useCallback(async (visit: Visit, reason: string) => {
    if (!visit || !visit.id) {
      console.error('Invalid visit object for rejection');
      return;
    }

    clearActionError();
    setActionLoading({ reject: true });

    try {
      await rejectVisit(visit.id, reason);
      // Close modal after successful rejection
      closeDetailsModal();
      // Refresh data to show updated status
      await refreshData();
    } catch (error) {
      const errorHandler = StandardizedErrorHandler.getInstance();
      const appError = errorHandler.toAppError(error);
      let errorDetails: { product_id?: number; current_quantity?: number; reported_quantity?: number } | null = null;
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.error) {
          errorDetails = axiosError.response.data.error;
        }
      }
      setActionError(appError.message, errorDetails);
      errorHandler.handleError(error);
    } finally {
      setActionLoading({ reject: false });
    }
  }, [rejectVisit, closeDetailsModal, refreshData, clearActionError, setActionError, setActionLoading]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    clearErrors();
    clearVisitDetails();
    resetUI();
  }, [clearErrors, clearVisitDetails, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    isDetailsModalOpen, selectedVisit,
    statusFilter: visitsStore.filters.status ?? 'all',
    handleStatusTabChange,
    applyFilters, clearFilters,
    selectedPeriod, selectedStore, dateFrom, dateTo,
    lastVisit, visitHistory, storeVisitsLoading, storeVisitsError,
    actionError, actionErrorDetails, clearActionError,
    actionLoading,

    // Data
    visits, visitDetails, statistics: statsData, pagination,

    // Loading States
    loading, loadingDetails, loadingStatistics: statsLoading,

    // Error States
    error, detailsError, statisticsError: statsError,

    // Actions
    handleTabChange, handleVisitClick, handleCloseDetailsModal, handleRefresh,
    handlePageChange, handlePageSizeChange,
    handleApproveVisit, handleRejectVisit,
    fetchVisits, fetchVisitDetails, fetchStatistics: fetchStats,
    setSelectedPeriod, setSelectedStore, setDateFrom, setDateTo, clearAnalyticsFilters,
    setLastVisit, setVisitHistory, setStoreVisitsLoading, setStoreVisitsError, clearStoreVisits,
    refreshData, resetAllStores
  };
};
